import dashboardAPI from './index'
import { getTotalIncome } from '@/view/pages/dashboard-all/utils/totals'
import {
  MIN_BLOCK_DURATION_MS,
  groupRanges,
  runWithMinDuration,
} from '@/core/utils/requestPacing'
import {
  DAILY_SERIES_FIELD,
  normalizeAverageDailySeries,
  readDailyAverageValue,
} from '@/core/utils/dailyAverageFields'
import { formatYmd } from '@/core/utils/dashboardDates'

const DAILY_AVERAGE_KEYS = Object.keys(DAILY_SERIES_FIELD)

class ProgressiveDashboardAllLoader {
  constructor() {
    this.isLoading = false
    this.currentProgress = 0
    this.totalMonths = 0
    this.onProgressCallback = null
    this.onCompleteCallback = null
    this.onErrorCallback = null
  }

  calculateMonthsBetween(startDate, endDate) {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const yearDiff = end.getFullYear() - start.getFullYear()
    const monthDiff = end.getMonth() - start.getMonth()
    return yearDiff * 12 + monthDiff + 1
  }

  generateMonthlyRanges(startDate, endDate) {
    const ranges = []
    const start = new Date(startDate)
    const end = new Date(endDate)

    let current = new Date(start.getFullYear(), start.getMonth(), 1)

    while (current <= end) {
      const monthStart = new Date(current)
      const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0)

      const rangeStart = monthStart < start ? start : monthStart
      const rangeEnd = monthEnd > end ? end : monthEnd

      // Ignorar rangos inválidos. Y-m-d local (contrato §10); no toISOString (UTC).
      if (rangeStart <= rangeEnd) {
        ranges.push({
          start: formatYmd(rangeStart),
          end: formatYmd(rangeEnd),
          month: current.getMonth() + 1,
          year: current.getFullYear(),
        })
      }

      current.setMonth(current.getMonth() + 1)
    }

    return ranges
  }

  async loadMonthData(dateRange, facilities, userId = null) {
    const data = {
      start_date: dateRange.start,
      end_date: dateRange.end,
      facilities,
    }
    if (userId) data.user_id = userId

    const response = await dashboardAPI.getAllDashboardData(data)
    const unified = this.unifyAllResponse(response)
    // Normalizar antes de usar
    this.normalizeAverages(unified)
    this.normalizePatientSeries(unified)
    this.normalizeMemberships(unified)
    this.normalizeBonus(unified)
    return unified
  }

  unifyAllResponse(resp) {
    // Unifica el payload 'ALL' en un solo objeto plano como espera el dashboard
    const blocks = [
      resp?.allDataCount?.data,
      resp?.all_patients?.data,
      resp?.total_refunds?.data,
      resp?.values_totals,
      resp?.year_data?.data,
    ].filter(Boolean)

    const result = {}
    let mergedMemberships = null
    for (const blk of blocks) {
      // Merge plano para todo excepto memberships
      Object.keys(blk).forEach(k => {
        if (k !== 'memberships') {
          result[k] = blk[k]
        }
      })
      // Merge profundo de memberships
      if (blk.memberships) {
        if (!mergedMemberships) {
          mergedMemberships = JSON.parse(JSON.stringify(blk.memberships))
        } else {
          mergedMemberships.NP =
            parseInt(mergedMemberships.NP || 0) + parseInt(blk.memberships.NP || 0)
          mergedMemberships.NC =
            parseInt(mergedMemberships.NC || 0) + parseInt(blk.memberships.NC || 0)
          // data por ubicación
          if (!mergedMemberships.data) mergedMemberships.data = { NP: {}, NC: {} }
          const dataSrc = blk.memberships.data || {}
          ;['NP', 'NC'].forEach(t => {
            const src = dataSrc[t] || {}
            const dst = mergedMemberships.data[t] || {}
            Object.keys(src).forEach(loc => {
              dst[loc] = parseInt(dst[loc] || 0) + parseInt(src[loc] || 0)
            })
            mergedMemberships.data[t] = dst
          })
          // date por día
          const dateSrc = blk.memberships.date || {}
          if (dateSrc && Object.keys(dateSrc).length) {
            if (!mergedMemberships.date) mergedMemberships.date = {}
            Object.keys(dateSrc).forEach(dk => {
              const cur = mergedMemberships.date[dk] || { NP: 0, NC: 0 }
              const add = dateSrc[dk] || {}
              mergedMemberships.date[dk] = {
                NP: parseInt(cur.NP || 0) + parseInt(add.NP || 0),
                NC: parseInt(cur.NC || 0) + parseInt(add.NC || 0),
              }
            })
          }
        }
      }
    }
    if (mergedMemberships) result.memberships = mergedMemberships
    // Adjuntar bonus original si se requiere en UI
    if (resp?.bonus?.data) {
      // La API devuelve { data: { bonus: { ... } } }
      result.bonus = resp.bonus.data.bonus || resp.bonus.data
    }
    return result
  }

  combineMonthlyData(monthlyDataArray, dateRange = null) {
    if (monthlyDataArray.length === 0) return {}

    const combined = JSON.parse(JSON.stringify(monthlyDataArray[0]))

    for (let i = 1; i < monthlyDataArray.length; i++) {
      const monthData = monthlyDataArray[i]

      ;[
        'all_patients',
        'patients',
        'patients_per_day',
        'patients_per_month',
        'year_data',
      ].forEach(key => {
        if (combined[key] && monthData[key]) {
          combined[key] = combined[key].concat(monthData[key])
        }
      })

      if (combined.totals && monthData.totals) {
        ;['income', 'expenses', 'net_income'].forEach(k => {
          const before = parseFloat(combined.totals[k]) || 0
          const toAdd = parseFloat(monthData.totals[k]) || 0
          combined.totals[k] = before + toAdd
        })
      }
      if (combined.cash_cc && monthData.cash_cc) {
        ;['cash', 'cc'].forEach(k => {
          const before = parseFloat(combined.cash_cc[k]) || 0
          const toAdd = parseFloat(monthData.cash_cc[k]) || 0
          combined.cash_cc[k] = before + toAdd
        })
      }
      // Sumar devoluciones (aceptar ambas claves por inconsistencia del endpoint)
      const readRefund = obj =>
        parseFloat(
          obj?.total_refunds !== undefined
            ? obj.total_refunds
            : obj?.total_refounds !== undefined
            ? obj.total_refounds
            : 0
        ) || 0
      if (
        combined.total_refunds !== undefined ||
        combined.total_refounds !== undefined ||
        monthData.total_refunds !== undefined ||
        monthData.total_refounds !== undefined
      ) {
        const before = readRefund(combined)
        const toAdd = readRefund(monthData)
        combined.total_refunds = before + toAdd
        if (combined.total_refounds !== undefined) delete combined.total_refounds
      }

      if (combined.memberships && monthData.memberships) {
        ;['NP', 'NC'].forEach(type => {
          const before = parseInt(combined.memberships[type]) || 0
          const toAdd = parseInt(monthData.memberships[type]) || 0
          combined.memberships[type] = before + toAdd
        })

        if (combined.memberships.data && monthData.memberships.data) {
          ;['NP', 'NC'].forEach(type => {
            if (combined.memberships.data[type] && monthData.memberships.data[type]) {
              Object.keys(monthData.memberships.data[type]).forEach(location => {
                if (!combined.memberships.data[type][location]) {
                  combined.memberships.data[type][location] = 0
                }
                const before = combined.memberships.data[type][location]
                const toAdd = parseInt(monthData.memberships.data[type][location]) || 0
                combined.memberships.data[type][location] = before + toAdd
              })
            }
          })
        }

        // Fusionar mapa por fecha (date): sumar NP/NC por día
        const combinedDate =
          combined.memberships.date && typeof combined.memberships.date === 'object'
            ? combined.memberships.date
            : {}
        const monthDate =
          monthData.memberships.date && typeof monthData.memberships.date === 'object'
            ? monthData.memberships.date
            : {}
        Object.keys(monthDate).forEach(dateKey => {
          const cur = combinedDate[dateKey] || { NP: 0, NC: 0 }
          const add = monthDate[dateKey] || { NP: 0, NC: 0 }
          combinedDate[dateKey] = {
            NP: parseInt(cur.NP || 0) + parseInt(add.NP || 0),
            NC: parseInt(cur.NC || 0) + parseInt(add.NC || 0),
          }
        })
        combined.memberships.date = combinedDate
      }

      if (combined.average && monthData.average) {
        // A9: incluir series tipadas (fu/pi/pp/rs/cv), no solo total/nc/np
        DAILY_AVERAGE_KEYS.forEach(k => {
          if (
            monthData.average[k] &&
            Array.isArray(monthData.average[k])
          ) {
            if (!Array.isArray(combined.average[k])) {
              combined.average[k] = []
            }
            combined.average[k] = combined.average[k].concat(
              monthData.average[k]
            )
          }
        })
        this.mergeAverageSummary(combined.average, monthData.average)
        ;['np_30_count', 'nc_30_count'].forEach(key => {
          const before = parseInt(combined.average[key], 10) || 0
          const toAdd = parseInt(monthData.average[key], 10) || 0
          if (before || toAdd || combined.average[key] !== undefined) {
            combined.average[key] = before + toAdd
          }
        })
        if (monthData.average.by_type || combined.average.by_type) {
          combined.average.by_type = this.mergeAverageByType(
            combined.average.by_type,
            monthData.average.by_type
          )
        }
      }

      if (monthData.patients_by_type || combined.patients_by_type) {
        combined.patients_by_type = this.mergePatientsByType(
          combined.patients_by_type,
          monthData.patients_by_type
        )
      }

      if (combined.stadistics && monthData.stadistics) {
        Object.keys(monthData.stadistics).forEach(key => {
          if (
            typeof combined.stadistics[key] === 'number' &&
            typeof monthData.stadistics[key] === 'number'
          ) {
            combined.stadistics[key] =
              combined.stadistics[key] + monthData.stadistics[key]
          }
        })
      }
      if (combined.statistics && monthData.statistics) {
        Object.keys(monthData.statistics).forEach(key => {
          if (
            typeof combined.statistics[key] === 'number' &&
            typeof monthData.statistics[key] === 'number'
          ) {
            combined.statistics[key] =
              combined.statistics[key] + monthData.statistics[key]
          }
        })
      }

      if (combined.bonus && monthData.bonus) {
        Object.keys(monthData.bonus).forEach(location => {
          if (!combined.bonus[location]) combined.bonus[location] = {}
          Object.keys(monthData.bonus[location]).forEach(date => {
            if (!combined.bonus[location][date]) {
              combined.bonus[location][date] = JSON.parse(
                JSON.stringify(monthData.bonus[location][date])
              )
            } else {
              const existingDate = combined.bonus[location][date]
              const newDate = monthData.bonus[location][date]
              existingDate.total =
                (parseFloat(existingDate.total) || 0) + (parseFloat(newDate.total) || 0)
              existingDate.total_to_pay =
                (parseFloat(existingDate.total_to_pay) || 0) +
                (parseFloat(newDate.total_to_pay) || 0)
              if (existingDate.appointments && newDate.appointments) {
                existingDate.appointments = existingDate.appointments.concat(
                  newDate.appointments
                )
              } else if (newDate.appointments) {
                existingDate.appointments = newDate.appointments
              }
            }
          })
        })
      }
    }

    if (dateRange) combined.date_range = dateRange

    // Normalizar y ordenar series de promedios después de combinar
    this.normalizeAverages(combined)

    this.recalculateAverages(combined)
    this.calculateBackendLikeMetrics(combined)

    this.normalizePatientSeries(combined)
    this.normalizeMemberships(combined)
    this.normalizeBonus(combined)

    return combined
  }

  normalizeAverages(target) {
    try {
      if (!target || typeof target !== 'object') return
      if (!target.average || typeof target.average !== 'object') return
      // A9: alias correctos AVG_FU/PI/PP/RS/CV (+ remap legacy AVG_NP)
      normalizeAverageDailySeries(target.average)
    } catch (e) {}
  }

  mergeSummaryBucket(a = {}, b = {}) {
    const count = (parseInt(a.count, 10) || 0) + (parseInt(b.count, 10) || 0)
    const total = (parseFloat(a.total) || 0) + (parseFloat(b.total) || 0)
    return {
      count,
      total,
      average: count > 0 ? Math.round((total / count) * 100) / 100 : 0,
    }
  }

  mergePatientsByType(existing = [], incoming = []) {
    const map = new Map()
    ;[...(existing || []), ...(incoming || [])].forEach(item => {
      if (!item) return
      const key =
        item.type_visits_id != null
          ? `id:${item.type_visits_id}`
          : `p:${item.prefix || ''}`
      const prev = map.get(key)
      const count = parseInt(item.count, 10) || 0
      if (!prev) {
        map.set(key, {
          type_visits_id: item.type_visits_id,
          prefix: item.prefix,
          name: item.name,
          count,
        })
      } else {
        prev.count += count
        if (!prev.prefix && item.prefix) prev.prefix = item.prefix
        if (!prev.name && item.name) prev.name = item.name
      }
    })
    return Array.from(map.values())
  }

  mergeAverageByType(existing = [], incoming = []) {
    const map = new Map()
    ;[...(existing || []), ...(incoming || [])].forEach(item => {
      if (!item) return
      const key =
        item.type_visits_id != null
          ? `id:${item.type_visits_id}`
          : `p:${item.prefix || ''}`
      const prev = map.get(key)
      const count = parseInt(item.count, 10) || 0
      const total = parseFloat(item.total) || 0
      if (!prev) {
        map.set(key, {
          type_visits_id: item.type_visits_id,
          prefix: item.prefix,
          name: item.name,
          count,
          total,
          average: count > 0 ? Math.round((total / count) * 100) / 100 : 0,
        })
      } else {
        prev.count += count
        prev.total += total
        prev.average =
          prev.count > 0 ? Math.round((prev.total / prev.count) * 100) / 100 : 0
        if (!prev.prefix && item.prefix) prev.prefix = item.prefix
        if (!prev.name && item.name) prev.name = item.name
      }
    })
    return Array.from(map.values())
  }

  mergeAverageSummary(targetAverage, sourceAverage) {
    if (!targetAverage || !sourceAverage?.summary) return
    if (!targetAverage.summary) {
      targetAverage.summary = {
        total: { average: 0, count: 0, total: 0 },
      }
    }
    ;['total'].forEach(key => {
      if (!sourceAverage.summary[key] && !targetAverage.summary[key]) return
      targetAverage.summary[key] = this.mergeSummaryBucket(
        targetAverage.summary[key],
        sourceAverage.summary[key]
      )
    })
  }

  applySummaryAverages(average) {
    const summary = average?.summary
    if (!summary || typeof summary !== 'object') return false
    const bucket = summary.total
    if (!bucket || (bucket.count == null && bucket.total == null)) return false

    const count = parseInt(bucket.count, 10) || 0
    const total = parseFloat(bucket.total) || 0
    const avg =
      bucket.average != null && bucket.average !== ''
        ? parseFloat(bucket.average)
        : count > 0
        ? total / count
        : 0
    average.total = Math.trunc(avg || 0)
    return true
  }

  recalculateAverages(combinedData) {
    if (!combinedData.average) combinedData.average = {}
    const usedSummary = this.applySummaryAverages(combinedData.average)
    if (usedSummary) return

    const avgFromDaily = (listKey, fieldName, outKey) => {
      const list = combinedData.average[listKey]
      if (!list || !list.length) return
      const sum = list.reduce(
        (acc, item) => acc + readDailyAverageValue(item, fieldName),
        0
      )
      combinedData.average[outKey] = Math.round(sum / list.length)
    }

    avgFromDaily('total_daily', 'AVG_TOTAL', 'total')
    avgFromDaily('fu_daily', 'AVG_FU', 'fu')
    avgFromDaily('pi_daily', 'AVG_PI', 'pi')
    avgFromDaily('pp_daily', 'AVG_PP', 'pp')
    avgFromDaily('rs_daily', 'AVG_RS', 'rs')
    avgFromDaily('cv_daily', 'AVG_CV', 'cv')
  }

  calculateBackendLikeMetrics(combined) {
    try {
      if (combined.date_range?.start && combined.date_range?.end) {
        const startD = new Date(combined.date_range.start)
        const endD = new Date(combined.date_range.end)
        const msPerDay = 24 * 60 * 60 * 1000
        const diffDays = Math.max(1, Math.floor((endD - startD) / msPerDay) + 1)

        const totalIncome = getTotalIncome(combined.totals)
        const totalExpenses = parseFloat(combined.totals?.expenses) || 0
        const totalRefunds = parseFloat(combined.total_refunds) || 0
        const cashTotal = parseFloat(combined.cash_cc?.cash) || 0
        const ccTotal = parseFloat(combined.cash_cc?.cc) || 0
        const visitCount =
          parseInt(combined.average?.summary?.total?.count, 10) || 0
        const denom = visitCount > 0 ? visitCount : 1

        const netIncome = totalIncome - totalExpenses - totalRefunds

        combined.average = combined.average || {}
        combined.average.income_days = Math.round(totalIncome / diffDays)
        combined.average.income_day = visitCount > 0 ? totalIncome / visitCount : 0
        combined.average.expenses_days = Math.round(totalExpenses / diffDays)
        combined.average.expenses_day = totalExpenses / denom
        combined.average.net_income_days = Math.round(netIncome / diffDays)
        combined.average.net_income_day = visitCount > 0 ? netIncome / visitCount : 0

        combined.average.cash_day = cashTotal / denom
        combined.average.cc_day = ccTotal / denom
        combined.average.cash_days = Math.round(cashTotal / diffDays)
        combined.average.cc_days = Math.round(ccTotal / diffDays)
      }
    } catch (e) {}
  }

  normalizePatientSeries(target) {
    try {
      const containers = []
      if (target && typeof target === 'object') containers.push(target)
      if (target && typeof target.values_totals === 'object')
        containers.push(target.values_totals)
      if (target && target.year_data && typeof target.year_data.data === 'object')
        containers.push(target.year_data.data)

      containers.forEach(container => {
        if (Array.isArray(container.patients_per_day)) {
          container.patients_per_day = container.patients_per_day.map(it => ({
            year: String(it.year ?? ''),
            month: String(it.month ?? '').padStart(2, '0'),
            day: String(it.day ?? '').padStart(2, '0'),
            count: Number(it.count ?? it.total ?? it.TOTAL ?? 0),
          }))
        }
        if (Array.isArray(container.patients_per_month)) {
          container.patients_per_month = container.patients_per_month.map(it => ({
            year: String(it.year ?? ''),
            month: String(it.month ?? '').padStart(2, '0'),
            count: Number(it.count ?? it.total ?? it.TOTAL ?? 0),
          }))
        }
      })
    } catch (e) {}
  }

  normalizeMemberships(target) {
    try {
      if (!target || typeof target !== 'object') return
      if (!target.memberships) return
      if (!target.memberships.data || typeof target.memberships.data !== 'object') {
        target.memberships.data = { NP: {}, NC: {} }
      } else {
        const data = target.memberships.data
        if (!data.NP || typeof data.NP !== 'object' || Array.isArray(data.NP))
          data.NP = {}
        if (!data.NC || typeof data.NC !== 'object' || Array.isArray(data.NC))
          data.NC = {}
      }
      target.memberships.NP = parseInt(target.memberships.NP) || 0
      target.memberships.NC = parseInt(target.memberships.NC) || 0

      // Normalizar mapa por fecha si existe
      if (target.memberships.date && typeof target.memberships.date === 'object') {
        const normalized = {}
        Object.entries(target.memberships.date)
          .filter(([key]) => key !== '__ob__')
          .forEach(([dateKey, values]) => {
            // Espera claves como YYYY-M-D o YYYY-MM-DD
            const [y, m, d] = String(dateKey).split('-')
            const normKey = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(
              2,
              '0'
            )}`
            const valNC = parseInt(values?.NC) || 0
            const valNP = parseInt(values?.NP) || 0
            normalized[normKey] = { NC: valNC, NP: valNP }
          })
        target.memberships.date = normalized
      }
    } catch (e) {}
  }

  normalizeBonus(target) {
    try {
      if (!target || typeof target !== 'object') return
      if (!target.bonus || typeof target.bonus !== 'object') {
        target.bonus = {}
        return
      }
      const normalized = {}
      const isDateKey = key => /^(\d{4})-(\d{1,2})-(\d{1,2})$/.test(String(key))
      Object.entries(target.bonus).forEach(([location, dates]) => {
        if (!dates || typeof dates !== 'object') return
        const dateObj = {}
        Object.entries(dates)
          .filter(([k]) => k !== '__ob__' && isDateKey(k))
          .forEach(([dateKey, payload]) => {
            const [y, m, d] = String(dateKey).split('-')
            const normKey = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(
              2,
              '0'
            )}`
            const total = parseFloat(payload?.total) || 0
            const total_to_pay =
              parseFloat(payload?.total_to_pay ?? payload?.total_to_pay) || total
            const appointments = Array.isArray(payload?.appointments)
              ? payload.appointments
              : []
            dateObj[normKey] = { total, total_to_pay, appointments }
          })
        normalized[location] = dateObj
      })
      target.bonus = normalized
    } catch (e) {}
  }

  async loadProgressiveData({ start, end, facilities }, userId = null) {
    const monthsBetween = this.calculateMonthsBetween(start, end)

    if (monthsBetween <= 1) {
      // IMPORTANTE: Para <= 1 mes devolvemos la respuesta cruda
      // y dejamos que el frontend (dashboard-all.vue) haga la unificación
      // como en el flujo tradicional no progresivo.
      const resp = await dashboardAPI.getAllDashboardData({
        start_date: start,
        end_date: end,
        facilities,
        user_id: userId || undefined,
      })
      return resp
    }

    this.isLoading = true
    this.totalMonths = monthsBetween
    this.currentProgress = 0

    try {
      const monthlyRanges = groupRanges(this.generateMonthlyRanges(start, end))
      this.totalMonths = monthlyRanges.length
      const monthlyDataArray = []

      for (let i = 0; i < monthlyRanges.length; i++) {
        const range = monthlyRanges[i]
        this.currentProgress = i + 1
        if (this.onProgressCallback) {
          this.onProgressCallback({
            current: this.currentProgress,
            total: this.totalMonths,
            // Mantener visible el modal hasta que el caller procese datos
            percentage: Math.min(
              99,
              Math.round((this.currentProgress / this.totalMonths) * 100)
            ),
            currentMonth: range.month,
            currentYear: range.year,
          })
        }

        const isLastMonth = i === monthlyRanges.length - 1
        const monthData = await runWithMinDuration(
          () => this.loadMonthData(range, facilities, userId),
          isLastMonth ? 0 : MIN_BLOCK_DURATION_MS
        )
        monthlyDataArray.push(monthData)
      }

      const combinedData = this.combineMonthlyData(monthlyDataArray, { start, end })

      this.isLoading = false
      if (this.onCompleteCallback) {
        this.onCompleteCallback(combinedData)
      }
      if (this.onProgressCallback) {
        // Señal final: 100% para cerrar el modal tras procesar datos
        this.onProgressCallback({
          current: this.totalMonths,
          total: this.totalMonths,
          percentage: 100,
          currentMonth: 0,
          currentYear: 0,
        })
      }

      return combinedData
    } catch (error) {
      this.isLoading = false
      if (this.onProgressCallback) {
        this.onProgressCallback({
          current: 0,
          total: 0,
          percentage: 0,
          currentMonth: 0,
          currentYear: 0,
        })
      }
      if (this.onErrorCallback) {
        this.onErrorCallback(error)
      }
      throw error
    }
  }

  onProgress(callback) {
    this.onProgressCallback = callback
  }
  onComplete(callback) {
    this.onCompleteCallback = callback
  }
  onError(callback) {
    this.onErrorCallback = callback
  }
}

const progressiveLoaderAll = new ProgressiveDashboardAllLoader()
export default progressiveLoaderAll
